// Smart Flow Print Agent (PC do caixa): http://localhost:9123 (loopback é permitido em páginas https)
// Converte o HTML do cupom em texto de 48 colunas e envia RAW para a impressora — sem diálogo.
export const AGENT_URL = 'http://localhost:9123';
let COLS = 48;
export const setReceiptColumns = (n) => { COLS = [32, 42, 48].includes(Number(n)) ? Number(n) : 48; };
export const getReceiptColumns = () => COLS;
// Régua de teste: mostra a largura real da impressora (a última linha inteira indica as colunas)
export const columnsTestText = (cols) => {
  const ruler = (n) => `${n} colunas:\n${Array.from({ length: n }, (_, i) => String((i + 1) % 10)).join('')}`;
  return ['SMART FLOW - TESTE DE LARGURA', '', 'Escolha em Configuracoes a MAIOR', 'regua que saiu em UMA linha so:', '', ruler(32), '', ruler(42), '', ruler(48), '', `Configurado agora: ${cols} colunas`].join('\n');
};

// Estado persistido em localStorage: sobrevive a F5 e à navegação entre PDV/Configurações.
const LS_KEY = 'sf_print_agent';
const loadCache = () => {
  try {
    const j = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
    if (j && typeof j.ok === 'boolean') return j;
  } catch (_e) { /* noop */ }
  return { ok: null, at: 0, info: null };
};
let cache = loadCache();
const saveCache = (c) => {
  cache = c;
  try { localStorage.setItem(LS_KEY, JSON.stringify(c)); } catch (_e) { /* noop */ }
};

const withTimeout = (ms) => {
  const c = new AbortController();
  const t = setTimeout(() => c.abort(), ms);
  return { signal: c.signal, clear: () => clearTimeout(t) };
};

// Chrome 142+: página https só acessa http://localhost com permissão "Apps no dispositivo" (Local Network Access).
// targetAddressSpace dispara o pedido de permissão corretamente e libera o http em página https.
const LNA = { targetAddressSpace: 'loopback' };

// O /health do agente consulta as impressoras (WMI) e pode levar 1–3s na primeira chamada;
// por isso usamos timeout generoso + novas tentativas em vez de desistir em 900ms.
const fetchHealth = async (ms) => {
  const { signal, clear } = withTimeout(ms);
  try {
    const r = await fetch(`${AGENT_URL}/health`, { signal, cache: 'no-store', ...LNA });
    const j = await r.json();
    return j && j.ok ? j : null;
  } catch (_e) {
    return null;
  } finally { clear(); }
};

let inflight = null;
export const probeAgent = async (force = false) => {
  const ttl = cache.ok ? 5 * 60 * 1000 : 10 * 1000;
  if (!force && cache.ok !== null && Date.now() - cache.at < ttl) return cache.ok;
  if (inflight) return inflight;
  inflight = (async () => {
    // 1ª tentativa longa (dá tempo de o usuário aceitar a permissão do Chrome); depois insiste mais 2x.
    const attempts = [8000, 5000, 6000];
    let info = null;
    for (const ms of attempts) {
      info = await fetchHealth(ms);
      if (info) break;
    }
    saveCache({ ok: !!info, at: Date.now(), info });
    inflight = null;
    return cache.ok;
  })();
  return inflight;
};

// Aquecimento: chamado ao abrir o PDV para o agente já estar reconhecido na 1ª impressão.
export const warmAgent = () => { probeAgent(cache.ok !== true).catch(() => {}); };

export const agentInfo = () => cache.info;
export const agentKnownOnline = () => cache.ok === true;

export const printViaAgent = async (text, { cut = true, printer, drawer = false } = {}) => {
  const { signal, clear } = withTimeout(8000);
  try {
    // charset=utf-8 é OBRIGATÓRIO: sem ele o HttpListener do Windows decodifica o corpo em ANSI (Windows-1252)
    // e os bytes altos do comando QR (pL/pH do GS ( k) viram 2 caracteres → tamanho errado → a impressora
    // fica esperando dados e TRAVA (sintoma: cupom NFC-e para na chave de acesso e não sai o QR Code).
    const r = await fetch(`${AGENT_URL}/print`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      body: JSON.stringify({ text, cut, printer, drawer }),
      signal,
      ...LNA,
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok || !j.ok) throw new Error(j.error || `HTTP ${r.status}`);
    return j;
  } catch (e) {
    if (e?.name === 'AbortError' || e instanceof TypeError) saveCache({ ok: false, at: Date.now(), info: null });
    throw e;
  } finally { clear(); }
};

// ---------- HTML do cupom → texto monoespaçado (48 colunas) ----------
const ascii = (s) => String(s ?? '')
  .replace(/[·•]/g, '-').replace(/[—–]/g, '-').replace(/✓/g, 'OK').replace(/↳/g, '>').replace(/\u00A0/g, ' ')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/[^\x20-\x7E]/g, '');
const clean = (s) => ascii(s).replace(/\s+/g, ' ').trim();
const wrap = (s, indent = 0) => {
  const out = [];
  const width = COLS - indent;
  const words = clean(s).split(' ');
  let line = '';
  for (const w of words) {
    if (!w) continue;
    if ((line + ' ' + w).trim().length > width) {
      if (line) out.push(' '.repeat(indent) + line);
      line = w.length > width ? w.slice(0, width) : w;
    } else line = (line + ' ' + w).trim();
  }
  if (line) out.push(' '.repeat(indent) + line);
  return out;
};
const center = (s) => wrap(s).map((l) => ' '.repeat(Math.max(0, Math.floor((COLS - l.length) / 2))) + l);
const right = (s) => wrap(s).map((l) => ' '.repeat(Math.max(0, COLS - l.length)) + l);
// Destaque ESC/POS: negrito + altura/largura dupla (ESC ! 0x38) — a linha passa a ter COLS/2 colunas
const BIG_ON = '\x1b!\x38';
const BIG_OFF = '\x1b!\x00';
const big = (s) => {
  const half = Math.floor(COLS / 2);
  const words = clean(s).split(' ');
  const rows = [];
  let line = '';
  for (const w of words) {
    if ((line + ' ' + w).trim().length > half) { if (line) rows.push(line); line = w.slice(0, half); }
    else line = (line + ' ' + w).trim();
  }
  if (line) rows.push(line);
  return rows.map((l) => BIG_ON + ' '.repeat(Math.max(0, Math.floor((half - l.length) / 2))) + l + BIG_OFF);
};
const leftRight = (l, r) => {
  l = clean(l); r = clean(r);
  if (!r) return wrap(l);
  if (l.length + r.length + 1 <= COLS) return [l + ' '.repeat(COLS - l.length - r.length) + r];
  return [...wrap(l), ' '.repeat(Math.max(0, COLS - r.length)) + r];
};

// ---------- QR Code nativo ESC/POS (GS ( k) ----------
// O agente codifica o texto em CP850 antes de enviar à impressora; para gerar um byte 0x80–0xFF
// (ex.: pL do tamanho do QR) usamos o caractere Unicode que o CP850 mapeia para esse byte.
const CP850_HI = '\u00c7\u00fc\u00e9\u00e2\u00e4\u00e0\u00e5\u00e7\u00ea\u00eb\u00e8\u00ef\u00ee\u00ec\u00c4\u00c5\u00c9\u00e6\u00c6\u00f4\u00f6\u00f2\u00fb\u00f9\u00ff\u00d6\u00dc\u00f8\u00a3\u00d8\u00d7\u0192\u00e1\u00ed\u00f3\u00fa\u00f1\u00d1\u00aa\u00ba\u00bf\u00ae\u00ac\u00bd\u00bc\u00a1\u00ab\u00bb\u2591\u2592\u2593\u2502\u2524\u00c1\u00c2\u00c0\u00a9\u2563\u2551\u2557\u255d\u00a2\u00a5\u2510\u2514\u2534\u252c\u251c\u2500\u253c\u00e3\u00c3\u255a\u2554\u2569\u2566\u2560\u2550\u256c\u00a4\u00f0\u00d0\u00ca\u00cb\u00c8\u0131\u00cd\u00ce\u00cf\u2518\u250c\u2588\u2584\u00a6\u00cc\u2580\u00d3\u00df\u00d4\u00d2\u00f5\u00d5\u00b5\u00fe\u00de\u00da\u00db\u00d9\u00fd\u00dd\u00af\u00b4\u00ad\u00b1\u2017\u00be\u00b6\u00a7\u00f7\u00b8\u00b0\u00a8\u00b7\u00b9\u00b3\u00b2\u25a0\u00a0';
const byteChar = (b) => (b < 0x80 ? String.fromCharCode(b) : CP850_HI[b - 0x80]);
export const QR_MARKER_RE = /\[\[QR:([^\]]*)\]\]/g;
// Bytes do comando QR Code ESC/POS (GS ( k): modelo 2, módulo `size`, correção L, armazena e imprime
export const escposQrBytes = (data, size = 6) => {
  const d = String(data || '').replace(/[^\x20-\x7E]/g, '');
  if (!d) return [];
  const len = d.length + 3;
  const GSK = [0x1d, 0x28, 0x6b];
  const str = (s) => Array.from(s, (c) => c.charCodeAt(0));
  return [
    0x1b, 0x61, 0x01,                                   // centraliza
    ...GSK, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00,         // modelo 2
    ...GSK, 0x03, 0x00, 0x31, 0x43, size,               // tamanho do módulo
    ...GSK, 0x03, 0x00, 0x31, 0x45, 0x30,               // correção de erro L
    ...GSK, len & 0xff, len >> 8, 0x31, 0x50, 0x30, ...str(d), // armazena dados
    ...GSK, 0x03, 0x00, 0x31, 0x51, 0x30,               // imprime
    0x0a, 0x1b, 0x61, 0x00,                             // volta ao alinhamento normal
  ];
};
// Agente antigo (1.2): bytes viram caracteres que o CP850 do agente converte de volta (exige charset=utf-8 no POST)
export const escposQr = (data, size = 6) => escposQrBytes(data, size).map(byteChar).join('');
// Agente 1.3+: bytes brutos em base64 — nenhuma conversão de charset no caminho (à prova de travamento)
export const escposQrB64 = (data, size = 6) => {
  const bytes = escposQrBytes(data, size);
  return bytes.length ? `[[B64:${btoa(String.fromCharCode(...bytes))}]]` : '';
};
// Substitui os marcadores [[QR:...]] do texto pelo comando ESC/POS (modo Print Agent)
export const inlineQrCodes = (text, { rawB64 = !!cache.info?.raw_b64 } = {}) =>
  text.replace(QR_MARKER_RE, (_m, data) => (rawB64 ? escposQrB64 : escposQr)(decodeURIComponent(data)));

const BLOCK = new Set(['DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'TABLE', 'TBODY', 'THEAD', 'TR', 'LI', 'UL', 'OL', 'SECTION', 'HEADER', 'FOOTER', 'HR', 'BR']);
const SKIP = new Set(['SCRIPT', 'STYLE', 'IMG', 'SVG', 'HEAD', 'TITLE', 'META', 'LINK']);
const hasClass = (el, ...names) => names.some((n) => el.classList && el.classList.contains(n));
const hasBlockChild = (el) => Array.from(el.children || []).some((c) => BLOCK.has(c.tagName));

export const htmlToReceiptText = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const lines = [];
  const push = (arr) => arr.forEach((l) => lines.push(l));

  const renderBlock = (el, inherit = {}) => {
    const tag = el.tagName;
    if (SKIP.has(tag)) return;
    if (tag === 'HR' || hasClass(el, 'hr')) { lines.push('-'.repeat(COLS)); return; }
    if (hasClass(el, 'sign-line')) { lines.push(''); lines.push('_'.repeat(COLS)); return; }
    if (tag === 'BR') { lines.push(''); return; }
    if (hasClass(el, 'cust-name-big')) { push(big(el.textContent)); return; }
    if (hasClass(el, 'qr') && el.dataset && el.dataset.qr) { lines.push(`[[QR:${encodeURIComponent(el.dataset.qr)}]]`); return; }

    if (tag === 'TR') {
      const cells = Array.from(el.children).filter((c) => c.tagName === 'TD' || c.tagName === 'TH');
      if (cells.length >= 2) {
        const first = cells[0];
        const blocks = hasBlockChild(first) ? Array.from(first.children).map((c) => clean(c.textContent)).filter(Boolean) : [clean(first.textContent)];
        const r = cells.slice(1).map((c) => clean(c.textContent)).join(' ');
        push(leftRight(blocks[0] || '', r));
        blocks.slice(1).forEach((b) => push(wrap(b, 3)));
        return;
      }
      if (cells.length === 1) { renderBlock(cells[0], inherit); return; }
    }

    if (hasClass(el, 'row')) {
      const spans = Array.from(el.children).filter((c) => !SKIP.has(c.tagName));
      if (spans.length >= 2) {
        push(leftRight(spans[0].textContent, spans.slice(1).map((s) => s.textContent).join(' ')));
        return;
      }
    }

    const centered = inherit.center || hasClass(el, 'center', 'warn', 'info', 'access-key', 'cust-name-big') || tag === 'H1';
    const rightAl = hasClass(el, 'right');

    if (hasBlockChild(el)) {
      Array.from(el.childNodes).forEach((n) => {
        if (n.nodeType === 3) { const t = clean(n.textContent); if (t) push(centered ? center(t) : wrap(t)); }
        else if (n.nodeType === 1) renderBlock(n, { center: centered });
      });
      if (hasClass(el, 'warn', 'info', 'sign-area')) lines.push('');
      return;
    }

    const text = clean(el.textContent);
    if (!text) return;
    if (hasClass(el, 'warn', 'info')) { lines.push(''); push(center(text)); lines.push(''); return; }
    if (rightAl) push(right(text));
    else if (centered) push(center(text));
    else push(wrap(text));
  };

  renderBlock(doc.body);
  // colapsa linhas em branco repetidas
  const out = [];
  for (const l of lines) { if (l === '' && out[out.length - 1] === '') continue; out.push(l); }
  while (out.length && out[0] === '') out.shift();
  return out.join('\n');
};
